import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Crosshair } from "@/components/Crosshair";
import { AccentText } from "@/lib/pageContent";
import { Save, Loader2, RotateCcw, Upload, ExternalLink, ImageOff } from "lucide-react";

const BTN = "font-mono uppercase text-[10px] font-bold px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5";
const INPUT = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm";

function ImageField({ value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/pages/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="flex items-start gap-3">
      <div className="w-28 h-20 shrink-0 border border-sniper bg-obsidian overflow-hidden flex items-center justify-center">
        {value ? <img src={value} alt="" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
          : <ImageOff className="w-6 h-6 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL, or upload →"
          className={INPUT} data-testid={`${testid}-url`} />
        <div className="mt-2 flex items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" data-testid={`${testid}-file`} />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload`}
            className={`${BTN} border border-olive text-olive-bright hover:bg-olive/10`}>
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`}
              className={`${BTN} border border-sniper text-muted-foreground hover:text-foreground`}>Clear</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminPageEditor() {
  const { pageKey } = useParams();
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setDirty(false);
    try {
      const { data } = await api.get(`/admin/pages/${pageKey}`);
      setSchema(data);
      setValues(data.values || {});
      setDefaults(data.defaults || {});
    } catch (e) { toast.error(formatError(e)); setSchema(null); }
    finally { setLoading(false); }
  }, [pageKey]);

  useEffect(() => { load(); }, [load]);

  const setField = (key, val) => { setValues((v) => ({ ...v, [key]: val })); setDirty(true); };
  const resetField = (key) => setField(key, defaults[key] ?? "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/pages/${pageKey}`, { values });
      setValues(data.values || values);
      setDirty(false);
      toast.success("Saved — live now");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <p className="font-mono text-sm uppercase text-muted-foreground flex items-center gap-2" data-testid="page-editor-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>;
  }
  if (!schema) {
    return <p className="font-mono text-sm uppercase text-blaze" data-testid="page-editor-error">Couldn't load this page. Check the URL.</p>;
  }

  const accentFields = new Set(
    schema.groups.flatMap((g) => g.fields.filter((f) => f.type === "accent").map((f) => f.key))
  );

  return (
    <div data-testid={`admin-page-editor-${pageKey}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-foreground">
            {schema.label} <span className="text-muted-foreground text-lg">page</span>
          </h1>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            Edits save live — no draft step. {schema.note}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a href={schema.route} target="_blank" rel="noopener noreferrer" data-testid="page-editor-view"
            className={`${BTN} border border-sniper text-secondary-foreground hover:text-foreground hover:border-olive/60`}>
            <ExternalLink className="w-3.5 h-3.5" /> View page
          </a>
          <button onClick={save} disabled={saving || !dirty} data-testid="page-editor-save"
            className={`${BTN} bg-olive hover:bg-olive-bright text-white`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      <Crosshair className="my-5" />

      <div className="space-y-8 max-w-2xl">
        {schema.groups.map((g) => (
          <div key={g.label} data-testid={`page-editor-group-${g.label}`}>
            <h2 className="font-mono text-[11px] uppercase tracking-[2px] text-olive-bright mb-3">{g.label}</h2>
            <div className="space-y-4">
              {g.fields.map((f) => {
                const val = values[f.key] ?? "";
                const changed = (values[f.key] ?? "") !== (defaults[f.key] ?? "");
                return (
                  <div key={f.key} data-testid={`page-editor-field-${f.key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="font-mono text-[10px] uppercase tracking-[1.5px] text-secondary-foreground">{f.label}</label>
                      {changed && (
                        <button onClick={() => resetField(f.key)} data-testid={`page-editor-reset-${f.key}`}
                          className="font-mono text-[9px] uppercase text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                          <RotateCcw className="w-3 h-3" /> Reset
                        </button>
                      )}
                    </div>
                    {f.type === "image" ? (
                      <ImageField value={val} onChange={(v) => setField(f.key, v)} testid={`page-editor-image-${f.key}`} />
                    ) : (f.type === "textarea" || f.type === "accent") ? (
                      <textarea value={val} onChange={(e) => setField(f.key, e.target.value)} rows={f.type === "accent" ? 2 : 3}
                        className={`${INPUT} resize-y leading-relaxed`} data-testid={`page-editor-input-${f.key}`} />
                    ) : (
                      <input value={val} onChange={(e) => setField(f.key, e.target.value)}
                        className={INPUT} data-testid={`page-editor-input-${f.key}`} />
                    )}
                    {accentFields.has(f.key) && (
                      <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">
                        Preview:{" "}
                        <span className="text-foreground font-heading uppercase" data-testid={`page-editor-preview-${f.key}`}>
                          <AccentText text={val} />
                        </span>
                        <span className="ml-2 opacity-70">Wrap words in *asterisks* to highlight; new line = line break.</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
